import { environment } from '../../environments/environment'
import axios from 'axios'
import Helper from '../../utils/helper';

const apiEndpoint = environment.webservices.endpoint

// Dedupe concurrent getPatientTasks requests with identical params.
// Any second caller that fires while the first is still in flight gets the same promise
// back instead of triggering a duplicate HTTP request (e.g., StrictMode double-invoke,
// parent prefetch racing with a child effect).
const inFlightTasks: { [key: string]: Promise<any> | undefined } = {}

export class TaskService {
  // constructor() { }

  getTasks(assigneeId: string, isDueToday: boolean = false) {
    let endpoint = `${apiEndpoint}/tasks?assignee_uuid=${assigneeId}`

    if (isDueToday) {
      endpoint = `${endpoint}&assignment_date__eq__or=${new Date().toISOString().split("T")[0]}`; // assigned for today
      endpoint = `${endpoint}&due_date__eq__or=${new Date().toISOString().split("T")[0]}`; // due for today
    }

    return axios.get(endpoint)
  }

  getPatientTasks(assigneeId: string | null,
    patientRefId?: string | null,
    ref_id?: string | null,
    title?: string | null,
    priority?: string | null,
    programName?: string | null,
    stageName?: string | null,
    isExcludeOptional: boolean = false,
    isDueToday: boolean = false,
    isCompleted: boolean = false,
    is_updated_today: boolean = false,
    keyword?: string | null,
    sortBy?: string | null,
    sortOrder?: string | null,
    limit: number = 10,
    page: number = 1,
    subsampled: boolean = false,
    includeAdHocToday: boolean = false,
    includeTests: boolean = true,
    hospitalName?: string | null,
    isAssignedToday: boolean = false) {
    let endpoint = `${apiEndpoint}/tasks?assignee_uuid=${assigneeId}`;

    if (patientRefId)
      endpoint = `${endpoint}&patient_ref_id=${patientRefId}`;

    if (programName)
      endpoint = `${endpoint}&program_name=${programName}`;

    if (stageName)
      endpoint = `${endpoint}&stage_name=${stageName}`;

    if (isDueToday) {
      endpoint = `${endpoint}&due_date__eq__or=${Helper.getUTCDate('')}`;
    }

    if (isAssignedToday) {
      endpoint = `${endpoint}&assignment_date__eq=${Helper.getUTCDate('')}`;
    }

    if (isCompleted)
      endpoint = `${endpoint}&task_status__neq=Completed`;

    if (is_updated_today)
      endpoint = `${endpoint}&is_updated_today=False`;

    if (true === isExcludeOptional) {
      endpoint = `${endpoint}&exclude_optional=True`;
    }
    else {
      endpoint = `${endpoint}&exclude_optional=False`;
    }

    if (keyword && keyword.length > 0)
      endpoint = `${endpoint}&q=${keyword}`;

    if (title !== '')
      endpoint = `${endpoint}&inclusion_filter=${title}`

    if (priority !== '') {
      endpoint = `${endpoint}&priority=${priority}`
    }

    if (subsampled && subsampled === true) {
      endpoint = `${endpoint}&subsampled=True`
    }

    if (true === includeAdHocToday) {
      endpoint = `${endpoint}&include_adhoc_tasks_today=True`;
    }

    if (ref_id)
      endpoint = `${endpoint}&ref_id=${ref_id}&ref_type=diagnostic_test`;

    if (sortBy)
      endpoint = `${endpoint}&sortBy=${sortBy}`;

    if (sortBy && sortOrder)
      endpoint = `${endpoint}&sortOrder=${sortOrder}`;

    endpoint = `${endpoint}&limit=${limit}&page=${page}`;

    if (includeTests)
      endpoint = `${endpoint}&include_tests=True`;

    if (hospitalName)
      endpoint = `${endpoint}&hospital_name__in=${hospitalName}`;

    const cached = inFlightTasks[endpoint]
    if (cached) return cached
    const promise = axios.get(endpoint).finally(() => {
      delete inFlightTasks[endpoint]
    })
    inFlightTasks[endpoint] = promise
    return promise
  }
  addPatientTask(payload: any) {
    let endpoint = `${apiEndpoint}/tasks`
    return axios.post(endpoint, payload)
  }
  // Test/demo patients only — the server refuses non-test patients (403).
  completeAllPatientTasks(patientRefId: string) {
    const endpoint = `${apiEndpoint}/patients/${patientRefId}/tasks/complete-all`
    return axios.post(endpoint, {})
  }
  getTaskInfo(taskId: string, selectedPrograms: string[] = []) {
    let endpoint = ''
    if (selectedPrograms.length) {
      endpoint = `${apiEndpoint}/tasks/${taskId}?${selectedPrograms
        .map((e, i) => (i === 0 ? `program=${e}` : `&program=${e}`))
        .join('')}`
    }
    else {
      endpoint = `${apiEndpoint}/tasks/${taskId}`
    }
    return axios.get(endpoint)
  }
  postTaskLogAttempts(taskId: any, payload: any) {
    const endpoint = `${apiEndpoint}/tasks/${taskId}/attempts`
    return axios.post(endpoint, payload)
  }
  postTaskDelay(taskId: any, payload: any) {
    const endpoint = `${apiEndpoint}/tasks/${taskId}/delay`
    return axios.post(endpoint, payload)
  }
  putTaskSchedule(taskId: any, payload: any) {
    const endpoint = `${apiEndpoint}/tasks/${taskId}`
    return axios.put(endpoint, payload)
  }
  getTaskHistory(patient_id: any,) {
    let endpoint = `${apiEndpoint}/patients/${patient_id}/task-history`
    return axios.get(endpoint)
  }
  getTaskHistoryById(patient_id: any, task_id: any) {
    const endpoint = `${apiEndpoint}/patients/${patient_id}/task-history?task_id=${task_id}`
    return axios.get(endpoint)
  }
  getTaskPendingCount(enrollment_id: any) {
    const endpoint = `${apiEndpoint}/enrollments/${enrollment_id}/pending-task-count`
    return axios.get(endpoint)
  }
  deleteTask(task_id: string) {
    const endpoint = `${apiEndpoint}/tasks/${task_id}`
    return axios.delete(endpoint, {})
  }
  getPatietMigration() {
    let endpoint = `${apiEndpoint}/api/migration`
    return axios.get(endpoint)
  }
  addPatietMigration(payload: any) {
    let endpoint = `${apiEndpoint}/api/migration`
    return axios.post(endpoint, payload)
  }
  deletePatietMigration(payload: any) {
    let endpoint = `${apiEndpoint}/api/migration/${payload[0]}`
    return axios.delete(endpoint)
  }
  getTaskBPDeatils(task_id: any) {
    const endpoint = `${apiEndpoint}/tasks/${task_id}/bp-reports`
    return axios.get(endpoint)
  }
}